/** Minimal upload recovery metadata; never contains filenames, files or research text. */
export type OperationKind = "scan" | "checkup";
export type OperationCheckpoint = {
  kind: OperationKind;
  requestKey: string;
  phase: "unknown" | "accepted";
  jobId?: string;
};
type SavedReceipt = OperationCheckpoint & { version: 1; ownerId: string; updatedAt: number };
export interface OperationStore {
  read: () => { checkpoint: OperationCheckpoint | null; blocked: boolean; notice: string | null };
  write: (checkpoint: OperationCheckpoint | null) => boolean;
}
const PREFIX = "resguard:upload-operation:v1:";
const memory = new Map<string, SavedReceipt | null>();
const dirty = new Set<string>();
export const OPERATION_STORAGE_NOTICE = "浏览器无法保存最新恢复记录。请保留原请求编号；刷新后可能只能恢复较早状态。新任务会在恢复记录保存成功后才提交。";
export const validRequestKey = (value: unknown): value is string => typeof value === "string" && /^[A-Za-z0-9_-]{16,128}$/.test(value);
export const validOperationJobId = (value: unknown): value is string => typeof value === "string" && /^[A-Za-z0-9_-]{1,200}$/.test(value);

export function createRequestKey(): string {
  if (typeof crypto.randomUUID === "function") return crypto.randomUUID();
  const bytes = new Uint8Array(24);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, byte => byte.toString(16).padStart(2, "0")).join("");
}

export function createOperationStore(ownerId: string, kind: OperationKind): OperationStore {
  const key = PREFIX + encodeURIComponent(ownerId) + ":" + kind;
  function persist(value: SavedReceipt | null) {
    memory.set(key, value);
    try {
      if (value) sessionStorage.setItem(key, JSON.stringify(value));
      else sessionStorage.removeItem(key);
      dirty.delete(key);
      return true;
    } catch { dirty.add(key); return false; }
  }
  return {
    read() {
      let value: unknown;
      let notice: string | null = null;
      if (dirty.has(key)) {
        value = memory.get(key) ?? null;
        if (!persist(value as SavedReceipt | null)) notice = OPERATION_STORAGE_NOTICE;
      } else {
        try {
          const raw = sessionStorage.getItem(key);
          if (raw && raw.length > 2048) throw new Error("oversized");
          value = raw ? JSON.parse(raw) : null;
        } catch {
          // An unreadable/malformed receipt cannot authorize a replacement request.
          return { checkpoint: null, blocked: true, notice: "无法读取原请求恢复记录，请先核对已有任务。本页不会重新上传或扣费。" };
        }
      }
      if (value == null) return { checkpoint: null, blocked: false, notice };
      const receipt = value as Partial<SavedReceipt>;
      if (receipt.version !== 1 || receipt.ownerId !== ownerId || receipt.kind !== kind
          || !Number.isFinite(receipt.updatedAt) || !validRequestKey(receipt.requestKey)
          || (receipt.phase !== "unknown" && receipt.phase !== "accepted")
          || (receipt.phase === "accepted" && !validOperationJobId(receipt.jobId))) {
        return { checkpoint: null, blocked: true, notice: "原请求恢复记录不完整或账号不匹配，请先核对已有任务。本页不会重新提交。" };
      }
      return { checkpoint: { kind, requestKey: receipt.requestKey, phase: receipt.phase,
        ...(receipt.phase === "accepted" ? { jobId: receipt.jobId } : {}) }, blocked: false, notice };
    },
    write(checkpoint) {
      return persist(checkpoint ? {
        version: 1, ownerId, kind, updatedAt: Date.now(), requestKey: checkpoint.requestKey,
        phase: checkpoint.phase, ...(checkpoint.phase === "accepted" ? { jobId: checkpoint.jobId } : {}),
      } : null);
    },
  };
}
